import './style.scss';

import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import CancellationAlertHost from '@/app/cancellations/components/AlertHost';
import CommandPalette from '@/app/help-centre/palette/CommandPalette';
import { FeedbackProvider } from '@/components/feedback/FeedbackProvider';
import FeedbackWidget from '@/components/feedback/FeedbackWidget';
import WorkingForBar from '@/components/team-access/working-for-bar';
import YourAccessCard from '@/components/team-access/your-access-card';
import { IDLE_WINDOW_MS } from '@/session/activity';
import { startSessionKeeper } from '@/session/session-keeper';

import AppModal from '../components/reusable/modal';
import { useInactivityTimeout } from '../hooks/inactivity-timeout';
import { useModal } from '../hooks/modal';
import { useActions } from '../hooks/use-store-state';
import type { RootState } from '../store/configureStore';
import ChildWrapper from './child-wrapper';
import ElectionSelection from './election-selection';
import GlobalRightSection from './global-right-section';
import Header from './header';
import InactivityAlert from './inactivity-alert';
import { menuData } from './menu/components/data';
import SideMenu from './menu/Side';
import NotificationCard from './notification-card';
import { AllProviders } from './providers';
import type { IDashboard } from './utils';

const Dashboard = ({ adminRoute, children }: IDashboard) => {
	const globalState = useSelector((state: RootState) => state.global);
	const actions = useActions();
	const modalProps = useModal();

	const handleWarning = useCallback(() => {
		if (adminRoute) {
			modalProps.handleOpenModal('Inactivity Alert');
		}
	}, [adminRoute, modalProps]);

	const handleSignOut = useCallback(() => {
		actions?.auth_logout({});
	}, [actions]);

	const stayLoggedIn = useCallback(() => {
		modalProps.handleCloseModal();
	}, [modalProps]);

	const isInactive = useInactivityTimeout(IDLE_WINDOW_MS, handleWarning, globalState?.sessionTimeoutGlobal?.status);

	// Keeps an ACTIVE user's session alive across the JWT's fixed expiry; idle users are
	// left to the inactivity flow above. Both read the same activity store.
	useEffect(() => {
		if (!adminRoute) return undefined;
		return startSessionKeeper();
	}, [adminRoute]);

	return (
		<>
			<AppModal
				modalProps={modalProps}
				size='medium'
				disableClose
			>
				<InactivityAlert
					stayLoggedIn={stayLoggedIn}
					handleSignOut={handleSignOut}
					startTimer={isInactive}
				/>
			</AppModal>

			<FeedbackProvider>
				<AllProviders>
					<GlobalRightSection />

					<NotificationCard
						notice={globalState?.notificationGlobal?.notice || ''}
						status={globalState?.notificationGlobal?.status || false}
					/>

					{adminRoute ? <CancellationAlertHost /> : null}
					{/* Mounted once: owns the global ⌘K / Ctrl-K listener and the palette dialog. */}
					{adminRoute ? <CommandPalette /> : null}
					{adminRoute ? <YourAccessCard /> : null}

					<div className='main-container'>
						<div className='child-container'>
							{adminRoute ? <SideMenu menu={menuData} /> : null}
							<div className='main-content-container w-100'>
								{adminRoute ? <Header /> : null}
								{/* In flow under the header; renders nothing unless the user is a team member. */}
								{adminRoute ? <WorkingForBar /> : null}

								<ChildWrapper>
									<ElectionSelection />
									{children}
								</ChildWrapper>
							</div>
						</div>
					</div>
					<FeedbackWidget />
				</AllProviders>
			</FeedbackProvider>
		</>
	);
};

export default Dashboard;
